import { RetryNeededError } from './connector-fetcher';

/** Timeout in ms after which polling gives up. */
const RETRY_202_TIMEOUT_MS = 10 * 60 * 1_000; // 10 minutes

/**
 * Polling schedule: 5 s initial wait, then aggressive 2 s polls for
 * the first minute (expect most responses here), then relax to 4 s.
 */
function getRetryDelay(attempt: number): number {
    if (attempt === 0) {
        return 5_000;
    }
    if (attempt <= 25) {
        return 2_000;
    }
    return 4_000;
}

/**
 * Retries the given fetch function when the connector returns 202
 * (still downloading). Uses exponential backoff with a 10-minute
 * timeout.
 */
export async function withRetryOn202<T>(fetchFn: () => Promise<T>): Promise<T> {
    let elapsed = 0;
    let attempt = 0;

    while (elapsed < RETRY_202_TIMEOUT_MS) {
        try {
            const result = await fetchFn();
            return result;
        } catch (ex) {
            if (ex instanceof RetryNeededError) {
                const delay = getRetryDelay(attempt);
                if (elapsed + delay >= RETRY_202_TIMEOUT_MS) {
                    break;
                }
                await new Promise((r) => setTimeout(r, delay));
                elapsed += delay;
                attempt++;
            } else {
                throw ex;
            }
        }
    }

    throw new Error('Max retries reached, connector result is not ready!');
}
